// Spots payments that repeat: the same payee, a similar amount, at a regular gap
// (weekly, monthly, quarterly, half-yearly or yearly, with a few days of slack).
// Runs on the phone over bank SMS plus what is already saved; nothing is sent anywhere.
//
// findRecurring(payments, { tracked, dismissed, today }) -> suggestions
//   payments:  [{ payee, amount, date: "YYYY-MM-DD", type }]  (money that went out)
//   tracked:   bills already saved; anything already set up to repeat is skipped
//   dismissed: Set of suggestion keys the person said "no" to before
//
// Each suggestion: { key, payee, type, amount, repeatUnit, repeatEvery, label,
//   count, lastDate, nextDate }

// [name, repeatUnit, repeatEvery, typical gap in days, allowed slack in days, fewest payments to trust it]
const PATTERNS = [
  ["week", "days", 7, 7, 1, 3],
  ["month", "months", 1, 30.4, 4, 2],
  ["3 months", "months", 3, 91, 7, 2],
  ["6 months", "months", 6, 182, 10, 2],
  ["year", "years", 1, 365, 12, 2],
];

// With only two payments to go on, the amounts must be almost the same; with more,
// a utility bill that varies a little still counts.
const AMOUNT_MARGIN_FEW = 0.1;
const AMOUNT_MARGIN_MANY = 0.2;

const DAY = 86400000;
const toDate = (iso) => new Date(`${iso}T00:00:00`);
const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localToday = () => isoOf(new Date());

// "Netflix.com", "NETFLIX COM" and "netflix@icici" should all be one payee.
export const payeeKey = (payee) =>
  String(payee || "")
    .toLowerCase()
    .replace(/@.*$/, "")
    .replace(/\.(com|in|co)\b/g, "")
    .replace(/[^a-z]/g, "");

export const addInterval = (iso, unit, every, times = 1) => {
  const d = toDate(iso);
  if (unit === "days") return isoOf(new Date(d.getFullYear(), d.getMonth(), d.getDate() + every * times));
  const months = (unit === "years" ? 12 : 1) * every * times;
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d.getDate(), lastDay));
  return isoOf(target);
};

const median = (values) => {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

const patternFor = (gaps) => {
  const typical = median(gaps);
  return PATTERNS.find(([, , , days, slack]) => Math.abs(typical - days) <= slack && gaps.every((g) => Math.abs(g - days) <= slack));
};

export const recurringLabel = (unit, every) => {
  if (unit === "days") return every === 7 ? "every week" : `every ${every} days`;
  if (unit === "years") return every === 1 ? "every year" : `every ${every} years`;
  return every === 1 ? "every month" : `every ${every} months`;
};

// Already set up to repeat? Then there is nothing to suggest.
const isTracked = (key, amount, tracked) =>
  tracked.some((b) => {
    const repeating = (b.repeatUnit && b.repeatUnit !== "once") || b.sourceKind;
    if (!repeating) return false;
    return payeeKey(b.payee) === key || Math.abs(b.amount - amount) <= Math.max(1, amount * 0.02);
  });

export const findRecurring = (payments, { tracked = [], dismissed = new Set(), today = localToday() } = {}) => {
  const groups = new Map();
  for (const p of payments) {
    const key = payeeKey(p.payee);
    if (!key || !(p.amount > 0) || !p.date) continue;
    if (!groups.has(key)) groups.set(key, new Map());
    // The same payment can come from the inbox and from what's already saved: count it once.
    groups.get(key).set(`${p.date}|${Math.round(p.amount)}`, p);
  }

  const suggestions = [];
  for (const [key, byDay] of groups) {
    const list = [...byDay.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    if (list.length < 2) continue;

    // Two payments on the same day aren't a schedule; keep one per day.
    const days = [];
    for (const p of list) if (!days.length || days[days.length - 1].date !== p.date) days.push(p);
    if (days.length < 2) continue;

    const gaps = days.slice(1).map((p, i) => (toDate(p.date) - toDate(days[i].date)) / DAY);
    const pattern = patternFor(gaps);
    if (!pattern) continue;
    const [, repeatUnit, repeatEvery, , , fewest] = pattern;
    if (days.length < fewest) continue;

    // The gap between the smallest and largest payment, against the largest.
    const amounts = days.map((p) => p.amount);
    const margin = days.length >= 3 ? AMOUNT_MARGIN_MANY : AMOUNT_MARGIN_FEW;
    if (Math.max(...amounts) - Math.min(...amounts) > Math.max(...amounts) * margin) continue;

    const last = days[days.length - 1];
    const suggestionKey = `${key}|${repeatUnit}${repeatEvery}`;
    if (dismissed.has(suggestionKey) || isTracked(key, last.amount, tracked)) continue;

    // The next one on the same schedule that hasn't happened yet.
    let nextDate = addInterval(last.date, repeatUnit, repeatEvery);
    for (let n = 2; nextDate < today && n < 1000; n += 1) nextDate = addInterval(last.date, repeatUnit, repeatEvery, n);

    suggestions.push({
      key: suggestionKey,
      payee: last.payee,
      type: last.type,
      amount: last.amount,
      repeatUnit,
      repeatEvery,
      label: recurringLabel(repeatUnit, repeatEvery),
      count: days.length,
      lastDate: last.date,
      nextDate,
    });
  }
  return suggestions.sort((a, b) => b.count - a.count || (a.nextDate < b.nextDate ? -1 : 1));
};

// Suggestions the person turned down stay hidden on this phone.
const DISMISSED_KEY = "budgetsprint-recurring-dismissed";

export const readDismissed = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

export const rememberDismissed = (keys) => {
  try {
    const set = readDismissed();
    keys.forEach((k) => set.add(k));
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set].slice(-500)));
  } catch {
    // Without storage the suggestion may simply come back on the next scan.
  }
};
